import type { NavigateFunction } from "react-router-dom";
import { replaceOnNativeRoot } from "feat/nativeNavigation/authNavigation";
import { isPathTabDisabled } from "feat/layout/tab-bar/tabDisabled.store";
import { preloadRoute } from "routing/tree";
import { isChangingLanguage } from "./navigationGuards";
import { TRAILING_PANE_TRANSITION, type PushPathState } from "./performPush";
import { queueViewTransition } from "./viewTransitionQueue";

/**
 * The single replace implementation behind both `window.replacePath` and `useGlobalRouter`.
 * `trailingPaneOnly` slides only the trailing pane, for a replace that changes nothing else.
 * `transition` names a View Transition type to run in place of the slide.
 */
export async function performReplace(
  nav: NavigateFunction,
  path: string,
  state?: PushPathState,
  trailingPaneOnly = false,
  transition?: string,
): Promise<void> {
  if (isChangingLanguage()) return;
  if (isPathTabDisabled(path)) return;

  // On the pre-auth stack a replace resets the stack, and is declined outright while the
  // root controller swaps roots.
  if (await replaceOnNativeRoot(path)) return;

  await preloadRoute(path);

  const destinationPaths = path.split("/").length;
  const currentPaths = window.location.pathname.split("/").length;
  const animOption = destinationPaths > currentPaths ? "right" : "left";

  await queueViewTransition(
    async () => {
      nav(path, { replace: true, state });
    },
    transition ? [transition] : ["slide", animOption, ...(trailingPaneOnly ? [TRAILING_PANE_TRANSITION] : [])],
  );
}
